import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import { PlayArrow, Pause, FastForward, FastRewind } from '@mui/icons-material';
import { API_CONFIG } from '../../config/constants';
import { Mic } from 'lucide-react';
import SubtitleComponent from './SubtitleComponent';
import axios from 'axios';

// Cache global pour les URLs vidéo avec versioning - similaire à AudioPlayer
const videoCache = new Map();
const loadingPromises = new Map();

// Génère une clé de cache basée sur les propriétés qui affectent la vidéo
const generateCacheKey = (projectId, segment) => {
    const cacheableProperties = {
        file: segment.video_segment_file,
        lipsync_file: segment.lipsync_video_file,
        lipsync_processed: segment.lipsync_processed,
        last_modified: segment.last_modified || segment.updated_at,
        start: segment.start,
        end: segment.end,
        speaker: segment.speaker,
        _refresh: segment._forceRefresh || null
    };

    const propertiesString = JSON.stringify(cacheableProperties);

    try {
        const encodedString = encodeURIComponent(propertiesString);
        const hash = btoa(encodedString).slice(0, 16);
        return `${projectId}_video_${segment.video_segment_file}_${hash}`;
    } catch (e) {
        let hash = 0;
        for (let i = 0; i < propertiesString.length; i++) {
            const char = propertiesString.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return `${projectId}_video_${segment.video_segment_file}_${Math.abs(hash)}`;
    }
};

// Trouve une entrée de cache pour un segment
const findCacheEntryForSegment = (projectId, segmentFile) => {
    const prefix = `${projectId}_video_${segmentFile}_`;
    for (const [key, value] of videoCache.entries()) {
        if (key.startsWith(prefix)) {
            return { key, value };
        }
    }
    return null;
};

const VideoPlayer = forwardRef(({
    videoRef,
    projectId,
    subtitleSettings,
    subtitles,
    currentTime,
    duration,
    isPlaying,
    handlePlayPause,
    handleSkip,
    videoUrl,
    transcript = []
}, ref) => {
    const containerRef = useRef(null);
    const [containerSize, setContainerSize] = useState({ width: '100%', height: '100%' });
    const [controlsHeight, setControlsHeight] = useState(0);
    const [loading, setLoading] = useState(true);
    const [videoSegments, setVideoSegments] = useState([]);
    const [currentSegment, setCurrentSegment] = useState(null);
    const [isPreloading, setIsPreloading] = useState(false);
    const [cacheProgress, setCacheProgress] = useState(0);
    const [isCacheReady, setIsCacheReady] = useState(false);

    // Refs pour éviter les conditions de course
    const isPlayingRef = useRef(isPlaying);
    const currentSegmentRef = useRef(null);
    const playPromiseRef = useRef(null);
    const isMountedRef = useRef(true);
    const preloadVideoRefs = useRef({});

    // Log de montage/démontage
    useEffect(() => {
        console.log(`VideoPlayer MOUNTED - Project: ${projectId}`);
        isMountedRef.current = true;

        return () => {
            console.log(`VideoPlayer UNMOUNTING - Project: ${projectId}`);
            isMountedRef.current = false;
        };
    }, [projectId]);

    // Synchroniser les refs avec les props
    useEffect(() => {
        isPlayingRef.current = isPlaying;
    }, [isPlaying]);

    // Nettoie les anciennes versions d'un segment du cache
    const cleanOldVersionsFromCache = useCallback((projectId, segmentFile, currentCacheKey) => {
        const prefix = `${projectId}_video_${segmentFile}_`;
        const keysToDelete = [];

        for (const [key, url] of videoCache.entries()) {
            if (key.startsWith(prefix) && key !== currentCacheKey) {
                // Ne pas révoquer les URLs car elles viennent de l'API, pas de createObjectURL
                keysToDelete.push(key);
            }
        }

        keysToDelete.forEach(key => videoCache.delete(key));

        if (keysToDelete.length > 0) {
            console.log(`VideoPlayer: Cleaned ${keysToDelete.length} old versions of ${segmentFile}`);
        }
    }, []);

    // Télécharge et met en cache une URL vidéo
    const downloadAndCacheVideoUrl = useCallback(async (segment) => {
        if (!segment || !segment.video_segment_file) return null;

        const cacheKey = generateCacheKey(projectId, segment);

        if (videoCache.has(cacheKey)) {
            console.log(`VideoPlayer: Using cached URL: ${segment.video_segment_file}`);
            return videoCache.get(cacheKey);
        }

        if (loadingPromises.has(cacheKey)) {
            return loadingPromises.get(cacheKey);
        }

        const downloadPromise = (async () => {
            try {
                // Déterminer quel fichier utiliser
                const fileToUse = (segment.lipsync_processed && segment.lipsync_video_file)
                    ? segment.lipsync_video_file
                    : segment.video_segment_file;

                // Si on a une URL lip-sync préchargée, l'utiliser
                if (segment.lipsync_video_url && segment.lipsync_processed) {
                    videoCache.set(cacheKey, segment.lipsync_video_url);
                    cleanOldVersionsFromCache(projectId, segment.video_segment_file, cacheKey);
                    return segment.lipsync_video_url;
                }

                const token = localStorage.getItem('token');
                if (!token) throw new Error('No authentication token found');

                console.log(`VideoPlayer: Downloading URL for: ${fileToUse}`);

                const response = await axios.get(
                    `${API_CONFIG.BASE_URL}/get_file/${projectId}/${fileToUse}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        params: { _t: Date.now() }
                    }
                );

                if (!response.data?.url) {
                    throw new Error("Invalid response format - no URL");
                }

                const videoUrl = response.data.url;
                videoCache.set(cacheKey, videoUrl);
                cleanOldVersionsFromCache(projectId, segment.video_segment_file, cacheKey);

                console.log(`VideoPlayer: Cached URL for: ${fileToUse}`);
                return videoUrl;

            } catch (error) {
                console.error(`VideoPlayer: Failed to cache URL for: ${segment.video_segment_file}`, error);
                videoCache.delete(cacheKey);
                throw error;
            } finally {
                loadingPromises.delete(cacheKey);
            }
        })();

        loadingPromises.set(cacheKey, downloadPromise);
        return downloadPromise;
    }, [projectId, cleanOldVersionsFromCache]);

    // Récupère l'URL depuis le cache
    const getCachedVideoUrl = useCallback((segment) => {
        const cacheKey = generateCacheKey(projectId, segment);
        return videoCache.get(cacheKey);
    }, [projectId]);

    // Fonction pour jouer la vidéo de manière sécurisée
    const safePlay = useCallback(async () => {
        if (!videoRef.current || !isMountedRef.current) return;

        if (!videoRef.current.src || videoRef.current.src === '') {
            console.log(`VideoPlayer: Cannot play: no source`);
            return;
        }

        try {
            if (playPromiseRef.current) {
                await playPromiseRef.current;
            }

            if (!isPlayingRef.current || !videoRef.current) return;

            playPromiseRef.current = videoRef.current.play();
            await playPromiseRef.current;

        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error(`VideoPlayer: Error playing:`, error);
            }
        } finally {
            playPromiseRef.current = null;
        }
    }, []);

    // Fonction pour mettre en pause la vidéo
    const safePause = useCallback(async () => {
        if (!videoRef.current) return;

        try {
            if (playPromiseRef.current) {
                await playPromiseRef.current;
            }

            if (videoRef.current && !videoRef.current.paused) {
                videoRef.current.pause();
                console.log(`VideoPlayer: Paused video`);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error(`VideoPlayer: Error pausing:`, error);
            }
        }
    }, []);

    // Fonction pour charger et jouer un segment vidéo
    const loadAndPlayVideoSegment = useCallback(async (segment, time) => {
        if (!segment) {
            // Si pas de segment, nettoyer la vidéo et afficher l'écran par défaut
            if (videoRef.current) {
                await safePause();
                videoRef.current.src = '';
            }
            setCurrentSegment(null);
            currentSegmentRef.current = null;
            setLoading(false);
            return;
        }

        if (!segment || !isMountedRef.current) return;

        setLoading(true);
        await safePause();

        try {
            // Vérifier que le segment est toujours valide
            const isSegmentValid = videoSegments.some(s =>
                s.start === segment.start && s.speaker === segment.speaker
            );
            if (!isSegmentValid) {
                console.warn(`VideoPlayer: Segment no longer exists`);
                return;
            }

            // Obtenir l'URL vidéo (depuis cache ou téléchargement)
            let videoUrl = getCachedVideoUrl(segment);
            if (!videoUrl) {
                videoUrl = await downloadAndCacheVideoUrl(segment);
            }

            if (!videoRef.current || !isMountedRef.current || !videoUrl) return;

            // Charger la nouvelle source
            videoRef.current.src = videoUrl;

            // Attendre que la vidéo soit prête
            await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => reject(new Error('Load timeout')), 10000);

                const onCanPlay = () => {
                    clearTimeout(timeout);
                    videoRef.current.removeEventListener('canplay', onCanPlay);
                    videoRef.current.removeEventListener('error', onError);
                    resolve();
                };

                const onError = () => {
                    clearTimeout(timeout);
                    videoRef.current.removeEventListener('canplay', onCanPlay);
                    videoRef.current.removeEventListener('error', onError);
                    reject(new Error('Load error'));
                };

                videoRef.current.addEventListener('canplay', onCanPlay);
                videoRef.current.addEventListener('error', onError);
                videoRef.current.load();
            });

            // Positionner le temps
            const relativeTime = Math.max(0, time - segment.start);
            videoRef.current.currentTime = relativeTime;

            console.log(`VideoPlayer: Loaded segment: ${segment.video_segment_file}`);

            // Mettre à jour l'état du segment actuel
            currentSegmentRef.current = segment;
            setCurrentSegment(segment);

            // Jouer si nécessaire
            if (isPlayingRef.current) {
                await safePlay();
            }

        } catch (error) {
            console.error(`VideoPlayer: Error loading segment:`, error);
        } finally {
            setLoading(false);
        }
    }, [videoSegments, getCachedVideoUrl, downloadAndCacheVideoUrl, safePause, safePlay]);

    // Trouve le segment vidéo au temps donné
    const findVideoSegmentAtTime = useCallback((time) => {
        if (!videoSegments.length) return null;

        return videoSegments.find(segment =>
            time >= (segment.start - 0.01) && time < (segment.end + 0.01)
        );
    }, [videoSegments]);

    // Précharger tous les segments vidéo
    const preloadAllVideos = useCallback(async () => {
        if (window._undoInProgress) {
            console.log(`VideoPlayer: Undo in progress, skipping preload`);
            return;
        }

        const segments = videoSegments.filter(s => s.video_segment_file);
        console.log(`VideoPlayer: Starting preload of ${segments.length} video segments`);

        let loadedCount = 0;
        let skippedCount = 0;
        const batchSize = 2; // Plus petit batch pour les vidéos

        for (let i = 0; i < segments.length; i += batchSize) {
            if (window._undoInProgress) {
                console.log(`VideoPlayer: Undo started during preload, stopping`);
                return;
            }

            const batch = segments.slice(i, i + batchSize);

            await Promise.allSettled(
                batch.map(async (segment) => {
                    try {
                        if (window._undoInProgress) {
                            throw new Error('Undo in progress');
                        }

                        await downloadAndCacheVideoUrl(segment);
                        loadedCount++;
                        setCacheProgress(Math.round((loadedCount / segments.length) * 100));
                    } catch (error) {
                        if (error.message === 'Undo in progress') {
                            console.log(`VideoPlayer: Stopped preload due to undo`);
                            return;
                        }
                        if (error.message.includes('File not found after undo') ||
                            (error.response && error.response.status === 404)) {
                            skippedCount++;
                            console.log(`VideoPlayer: Skipped missing file: ${segment.video_segment_file}`);
                        } else {
                            console.warn(`VideoPlayer: Failed to preload ${segment.video_segment_file}:`, error);
                        }
                    }
                })
            );
        }

        if (!window._undoInProgress) {
            setIsCacheReady(true);
            console.log(`VideoPlayer: Preload complete: ${loadedCount}/${segments.length} (${skippedCount} skipped)`);
        }
    }, [videoSegments, downloadAndCacheVideoUrl]);

    // ✅ EFFET CORRIGÉ - Détection des changements dans le transcript
    useEffect(() => {
        if (!transcript || transcript.length === 0) {
            setVideoSegments([]);
            return;
        }

        // Extraire tous les segments qui ont des fichiers vidéo
        const segments = transcript.filter(segment =>
            segment.video_segment_file &&
            (segment.is_silent === undefined || segment.is_silent === true || segment.is_silent === false)
        );

        // Trier les segments par ordre chronologique
        segments.sort((a, b) => a.start - b.start);

        // Vérifier si les segments ont vraiment changé (éviter les re-renders inutiles)
        const segmentsKey = segments.map(s =>
            `${s.video_segment_file}_${s.start}_${s.end}_${s.lipsync_processed || false}_${s.last_modified || ''}`
        ).join('|');

        const currentSegmentsKey = videoSegments.map(s =>
            `${s.video_segment_file}_${s.start}_${s.end}_${s.lipsync_processed || false}_${s.last_modified || ''}`
        ).join('|');

        if (segmentsKey === currentSegmentsKey) {
            // Pas de changement réel, pas besoin de mettre à jour
            return;
        }

        // Vérifier si des segments ont changé (pour invalidation du cache SEULEMENT)
        segments.forEach(segment => {
            const newCacheKey = generateCacheKey(projectId, segment);
            const existingEntry = findCacheEntryForSegment(projectId, segment.video_segment_file);

            if (existingEntry && existingEntry.key !== newCacheKey) {
                console.log(`VideoPlayer: Segment modified, invalidating cache: ${segment.video_segment_file}`);
                videoCache.delete(existingEntry.key);
                // NE PAS recharger ici - laisser l'effet principal s'en charger
            }
        });

        setVideoSegments(segments);
        console.log("VideoPlayer: Video segments updated:", segments.length);
    }, [transcript, projectId]); // ✅ SEULEMENT ces deux dépendances !

    // Effet pour précharger au montage
    useEffect(() => {
        if (!isCacheReady && videoSegments.length > 0) {
            preloadAllVideos();
        }
    }, [preloadAllVideos, isCacheReady, videoSegments]);

    // EFFET PRINCIPAL - Gérer les changements de segment
    useEffect(() => {
        const segment = findVideoSegmentAtTime(currentTime);

        // Vérifier si on doit changer de segment
        if (segment?.start !== currentSegmentRef.current?.start ||
            segment?.speaker !== currentSegmentRef.current?.speaker) {

            const timeoutId = setTimeout(() => {
                loadAndPlayVideoSegment(segment, currentTime);
            }, 50);

            return () => clearTimeout(timeoutId);
        } else if (segment && videoRef.current && !videoRef.current.seeking) {
            // Ajuster le temps si nécessaire
            const relativeTime = currentTime - segment.start;
            if (Math.abs(videoRef.current.currentTime - relativeTime) > 0.1) {
                videoRef.current.currentTime = relativeTime;
            }
        }
    }, [currentTime, findVideoSegmentAtTime, loadAndPlayVideoSegment]);

    // Effet pour gérer play/pause
    useEffect(() => {
        const handlePlayPause = async () => {
            if (!currentSegmentRef.current) {
                // Pas de segment vidéo, s'assurer que la vidéo est en pause
                await safePause();
                return;
            }

            if (!videoRef.current?.src) {
                console.log(`VideoPlayer: No source, skipping play/pause`);
                return;
            }

            const shouldPlay = isPlaying &&
                currentTime >= currentSegmentRef.current.start &&
                currentTime < currentSegmentRef.current.end;

            if (shouldPlay) {
                await safePlay();
            } else {
                await safePause();
            }
        };

        handlePlayPause();
    }, [isPlaying, currentTime, safePlay, safePause]);

    // Interface impérative
    useImperativeHandle(ref, () => ({
        play: safePlay,
        pause: safePause,
        getCurrentSegment: () => currentSegmentRef.current,
        isCacheReady: () => isCacheReady,
        getCacheProgress: () => cacheProgress,

        resetCache: () => {
            console.log(`VideoPlayer: Resetting cache...`);

            const projectPrefix = `${projectId}_video_`;

            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.src = '';
            }

            // Nettoyer le cache pour ce projet
            const deletedKeys = [];
            for (const [key, url] of videoCache.entries()) {
                if (key.startsWith(projectPrefix)) {
                    videoCache.delete(key);
                    deletedKeys.push(key);
                }
            }

            console.log(`VideoPlayer: Deleted ${deletedKeys.length} cache entries`);

            setCurrentSegment(null);
            currentSegmentRef.current = null;
            setCacheProgress(0);
            setIsCacheReady(false);

            if (!window._undoInProgress) {
                setTimeout(() => {
                    preloadAllVideos();
                }, 500);
            }
        },

        invalidateSegment: (segmentFile) => {
            console.log(`VideoPlayer: Invalidating cache for: ${segmentFile}`);
            const projectPrefix = `${projectId}_video_${segmentFile}_`;
            const keysToDelete = [];

            for (const [key, url] of videoCache.entries()) {
                if (key.startsWith(projectPrefix)) {
                    keysToDelete.push(key);
                }
            }

            keysToDelete.forEach(key => videoCache.delete(key));
            console.log(`VideoPlayer: Deleted ${keysToDelete.length} cache entries for ${segmentFile}`);
        },

        forceReloadSegment: async (updatedSegment) => {
            console.log(`VideoPlayer: Force reload requested for: ${updatedSegment.video_segment_file}`);

            const projectPrefix = `${projectId}_video_${updatedSegment.video_segment_file}_`;
            for (const [key, url] of videoCache.entries()) {
                if (key.startsWith(projectPrefix)) {
                    videoCache.delete(key);
                    console.log(`VideoPlayer: Removed cache: ${key}`);
                }
            }

            if (currentSegmentRef.current?.video_segment_file === updatedSegment.video_segment_file) {
                await loadAndPlayVideoSegment(updatedSegment, currentTime);
            } else {
                await downloadAndCacheVideoUrl(updatedSegment);
            }
        }
    }), [safePlay, safePause, isCacheReady, cacheProgress, projectId, preloadAllVideos, currentTime, loadAndPlayVideoSegment, downloadAndCacheVideoUrl]);

    // Mettre à jour la taille du conteneur
    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current && (videoRef.current || !videoUrl)) {
                const containerWidth = containerRef.current.clientWidth;
                const containerHeight = containerRef.current.clientHeight;
                let videoAspectRatio = 16 / 9;

                if (videoRef.current) {
                    videoAspectRatio = videoRef.current.videoWidth / videoRef.current.videoHeight;
                }

                let newWidth = containerWidth;
                let newHeight = containerHeight - controlsHeight;

                if (newHeight > newWidth / videoAspectRatio) {
                    newHeight = newWidth / videoAspectRatio;
                } else {
                    newWidth = newHeight * videoAspectRatio;
                }

                setContainerSize({ width: `${newWidth}px`, height: `${newHeight}px` });
            }
        };

        const resizeObserver = new ResizeObserver(updateSize);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, [videoRef, controlsHeight, videoUrl]);

    // Mesurer la hauteur des contrôles
    useEffect(() => {
        if (containerRef.current) {
            const controls = containerRef.current.querySelector('.video-controls');
            if (controls) {
                setControlsHeight(controls.offsetHeight);
            }
        }
    }, []);

    // Nettoyage au démontage
    useEffect(() => {
        return () => {
            // Nettoyer les éléments vidéo préchargés
            Object.values(preloadVideoRefs.current).forEach(video => {
                if (video && video.parentNode) {
                    video.src = '';
                    video.parentNode.removeChild(video);
                }
            });

            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.src = '';
            }
        };
    }, []);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    return (
        <Box ref={containerRef} sx={{
            width: '100%',
            height: '100%',
            bgcolor: '#2D2D2D',
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
        }}>
            <Box sx={{ width: containerSize.width, height: containerSize.height, overflow: 'hidden', position: 'relative' }}>
                {currentSegment || videoUrl ? (
                    <>
                        <video
                            ref={videoRef}
                            muted={true}
                            preload='auto'
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                        {subtitleSettings?.enabled && (
                            <SubtitleComponent
                                projectId={projectId}
                                currentTime={currentTime}
                                settings={subtitleSettings}
                                subtitles={subtitles}
                                videoRef={videoRef}
                            />
                        )}
                        {loading && (
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: 'white'
                            }}>
                                <CircularProgress color="inherit" />
                            </Box>
                        )}
                    </>
                ) : (
                    <Box sx={{
                        width: '100%',
                        height: '100%',
                        bgcolor: '#000000',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <Mic size={48} color="rgba(255,255,255,0.7)" />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 2 }}>
                            Aucun segment vidéo à cette position
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box className="video-controls" sx={{
                width: '98%',
                bgcolor: '#2D2D2D',
                p: 1.5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {formatTime(currentTime)}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            onClick={() => handleSkip(-10)}
                            sx={{ color: 'white', p: 0.5 }}
                        >
                            <FastRewind fontSize="small" />
                        </IconButton>
                        <IconButton
                            onClick={handlePlayPause}
                            sx={{ color: 'white', mx: 1, p: 0.5 }}
                        >
                            {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                        </IconButton>
                        <IconButton
                            onClick={() => handleSkip(10)}
                            sx={{ color: 'white', p: 0.5 }}
                        >
                            <FastForward fontSize="small" />
                        </IconButton>
                    </Box>

                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {formatTime(duration)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;